import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import KanbanView from '@/components/pipeline/KanbanView';
import ListView from '@/components/pipeline/ListView';
import FollowUpView from '@/components/pipeline/FollowUpView';
import ContactSlideOver from '@/components/pipeline/ContactSlideOver';
import { Plus, KanbanSquare, List, Clock } from 'lucide-react';
import { DEAL_TYPES, DEAL_TYPE_COLOR } from '@/lib/constants';

const VIEWS = [
    { key: 'kanban', label: 'Kanban', icon: KanbanSquare },
    { key: 'list', label: 'List', icon: List },
    { key: 'followup', label: 'Follow-ups', icon: Clock },
];

export default function PipelinePage() {
    const { profile } = useAuth();
    const [params, setParams] = useSearchParams();
    const view = params.get('view') || 'kanban';
    const filterStage = params.get('stage') || '';
    const filterPriority = params.get('priority') || '';
    const filterDealType = params.get('dealType') || '';
    const [contacts, setContacts] = useState([]);
    const [agents, setAgents] = useState([]);
    const [editing, setEditing] = useState(null);
    const [slideOpen, setSlideOpen] = useState(false);

    const load = useCallback(async () => {
        if (!profile?.organization_id) return;
        let cq = supabase.from('pipeline_contacts').select('*').is('deleted_at', null).order('created_at', { ascending: false });
        if (profile.role !== 'admin') cq = cq.eq('assigned_to', profile.id);
        const [{ data: cs }, { data: ag }] = await Promise.all([
            cq,
            supabase.from('profiles').select('id, full_name, email, role, is_active'),
        ]);
        setContacts(cs || []);
        setAgents(ag || []);
    }, [profile?.organization_id, profile?.role, profile?.id]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!profile?.organization_id) return;
        const ch = supabase
            .channel(`pipeline-${profile.organization_id}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'pipeline_contacts', filter: `organization_id=eq.${profile.organization_id}` }, () => { load(); })
            .subscribe();
        return () => { supabase.removeChannel(ch); };
    }, [profile?.organization_id, load]);

    const filtered = useMemo(() => {
        return contacts.filter((c) => {
            if (filterStage && c.pipeline_stage !== filterStage) return false;
            if (filterPriority && c.priority !== filterPriority) return false;
            if (filterDealType && c.deal_type !== filterDealType) return false;
            return true;
        });
    }, [contacts, filterStage, filterPriority, filterDealType]);

    const setView = (v) => { params.set('view', v); setParams(params, { replace: true }); };

    return (
        <div data-testid="pipeline-page">
            <div className="sticky top-14 z-20 bg-[#f7f8fa]/95 backdrop-blur border-b border-gray-200">
                <div className="px-4 lg:px-6 py-3 flex flex-wrap items-center gap-3 max-w-[1600px] mx-auto">
                    <h1 className="text-xl font-bold tracking-tight text-aks-text">Pipeline</h1>
                    <div className="hidden md:block text-[12px] text-gray-500">{filtered.length} contacts{filterStage ? ` · ${filterStage}` : ''}{filterPriority ? ` · ${filterPriority}` : ''}</div>
                    <div className="ml-auto flex items-center gap-2">
                        <div className="flex bg-white border border-gray-200 rounded-md p-0.5" role="tablist">
                            {VIEWS.map((v) => {
                                const Icon = v.icon;
                                const active = view === v.key;
                                return (
                                    <button key={v.key} onClick={() => setView(v.key)} className={`h-8 px-3 inline-flex items-center gap-1.5 text-[12px] font-semibold rounded ${active ? 'bg-aks-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid={`view-${v.key}`}>
                                        <Icon className="h-3.5 w-3.5" /> {v.label}
                                    </button>
                                );
                            })}
                        </div>
                        <button onClick={() => { setEditing(null); setSlideOpen(true); }} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px] hover:brightness-105 aks-focus-ring" data-testid="add-contact-btn">
                            <Plus className="h-4 w-4" /> Add contact
                        </button>
                    </div>
                    <div className="w-full flex items-center gap-1.5">
                        <button
                            onClick={() => { params.delete('dealType'); setParams(params, { replace: true }); }}
                            className={`h-7 px-3 rounded-full text-[12px] font-semibold border transition ${!filterDealType ? 'bg-aks-navy text-white border-aks-navy' : 'border-gray-300 text-gray-600 hover:border-aks-navy/40'}`}
                        >All</button>
                        {DEAL_TYPES.map((dt) => {
                            const c = DEAL_TYPE_COLOR[dt];
                            const active = filterDealType === dt;
                            return (
                                <button
                                    key={dt}
                                    onClick={() => { params.set('dealType', dt); setParams(params, { replace: true }); }}
                                    className="h-7 px-3 rounded-full text-[12px] font-semibold border transition"
                                    style={active ? { background: c, color: '#fff', borderColor: c } : { borderColor: `${c}66`, color: c }}
                                >{dt}</button>
                            );
                        })}
                    </div>
                    {(filterStage || filterPriority) && (
                        <div className="w-full flex items-center gap-2">
                            {filterStage && <Chip onRemove={() => { params.delete('stage'); setParams(params, { replace: true }); }}>Stage: {filterStage}</Chip>}
                            {filterPriority && <Chip onRemove={() => { params.delete('priority'); setParams(params, { replace: true }); }}>Priority: {filterPriority}</Chip>}
                        </div>
                    )}
                </div>
            </div>
            <div className="max-w-[1600px] mx-auto pt-4">
                {view === 'kanban' && <KanbanView contacts={filtered} agents={agents} onChange={load} />}
                {view === 'list' && <ListView contacts={filtered} agents={agents} />}
                {view === 'followup' && <FollowUpView contacts={filtered} onChange={load} />}
            </div>
            <ContactSlideOver open={slideOpen} onClose={() => setSlideOpen(false)} contact={editing} agents={agents} onSaved={load} onDeleted={load} />
        </div>
    );
}

function Chip({ children, onRemove }) {
    return (
        <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-aks-gold-soft border border-[rgba(201,168,76,0.32)] text-aks-navy text-[11px] font-semibold">
            {children}
            <button onClick={onRemove} className="ml-1 text-aks-navy/70 hover:text-aks-navy">×</button>
        </span>
    );
}
